import React, { useEffect, useState } from 'react';
import { observer, inject } from 'mobx-react';
import mixpanel from 'mixpanel-browser';
import TextareaAutosize from 'react-textarea-autosize';
import classnames from 'classnames';

import AmountSelector from 'components/AmountSelector/AmountSelector';
import Currency from 'components/Currency/Currency';
import { ReactComponent as CauzeLogo } from 'assets/images/brand/cauze-logo-confirmed.svg';
import useDashboardStore from 'stores/DashboardStore';
import { useParams } from '@reach/router';
import { FRAME } from 'stores/CheckoutStoreV2';
import useReloadableGiftingToken from 'hooks/useReloadableGiftingToken';
import CauzeSpinner from 'components/CauzeSpinner/CauzeSpinner';

const QRGiftCheckoutView = ({ uiStore }) => {
  const { setProps } = useDashboardStore();
  const { token } = useParams();
  const [amount, setAmount] = useState(null);
  const [comment, setComment] = useState('');
  const [success, setSuccess] = useState(false);
  const { reloadableToken, loading } = useReloadableGiftingToken({
    token,
    noGift: true,
  });

  useEffect(() => {
    setProps({
      noSidebar: true,
      navbarProps: {
        hideLoginPrompt: true,
      },
    });
  }, []);

  const handleCheckout = () => {
    mixpanel.track('Feed Item Join Click', {
      amount: amount,
      comment: comment,
      token: token,
    });

    uiStore.openModal('CHECKOUT', {
      amount: amount,
      comment: comment,
      checkoutType: 'reloadable_gift',
      frame: FRAME.PAYMENT_METHOD,
      onCheckoutSuccess: () => {
        setSuccess(true);
      },
      token: token,
    });
  };

  if (!reloadableToken && token && !loading) {
    window.location.href = `/redeem?qr=${token}`;
    return <></>;
  }

  if (loading) {
    return <CauzeSpinner fullscreen />;
  }

  if (success) {
    return (
      <div className="qr-gift-checkout-view flex flex-col min-h-screen">
        <div className="flex-1 flex flex-col justify-center items-center px-5 py-8 max-w-2xl mx-auto w-full">
          <div className="w-full flex flex-col items-center gap-y-6">
            {/* Success Logo */}
            <div className="flex justify-center mb-2">
              <CauzeLogo />
            </div>

            {/* Success Message */}
            <div className="text-center text-gray-1b2 font-agenda-black text-2xl md:text-3xl leading-tight mb-2">
              SUCCESS!
            </div>

            {/* Confirmation Message */}
            <div className="text-center px-4 text-gray-1b2 font-agenda text-lg md:text-xl leading-tight mb-4">
              Your gift of <Currency amount={amount || 0} /> has been added
              successfully!
            </div>

            {/* Additional Info */}
            {comment && (
              <div className="text-center px-4 text-gray-1b2 font-agenda text-base leading-tight mb-6">
                "{comment}"
              </div>
            )}

            {/* Close/Continue Button */}
            <div className="w-full pt-4">
              <button
                className="cz-btn-brand w-full h-12 font-agenda-bold text-base uppercase"
                onClick={() => {
                  // Navigate or close modal
                  window.location.href = '/donate';
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="qr-gift-checkout-view flex flex-col min-h-screen">
      <div className="flex-1 flex flex-col items-center px-5 py-8 max-w-2xl mx-auto w-full">
        <div className="w-full flex flex-col gap-y-8">
          {/* Header */}
          <div className="text-center">
            <h1 className="font-agenda-bold text-2xl md:text-3xl mb-2">
              Gift Checkout
            </h1>
            <p className="font-agenda text-base text-gray-1b2">
              Choose your gift amount and add an optional message
            </p>
          </div>

          {/* Amount Selection */}
          <div className="flex flex-col gap-y-4">
            <label className="font-agenda-bold text-lg">
              Select Gift Amount
            </label>
            <AmountSelector
              amounts={[1000, 2500, 5000, 10000]}
              defaultAmount={null}
              sendDefaultUpdate={false}
              onAmountUpdate={(amt) => setAmount(amt)}
            />
          </div>

          {/* Comment Section */}
          <div className="flex flex-col gap-y-4">
            <label className="font-agenda-bold text-lg">
              Add a Comment (Optional)
            </label>
            <div className="relative border border-solid border-black rounded-[10px] overflow-hidden">
              <TextareaAutosize
                className={classnames(
                  'overflow-hidden focus:outline-none pt-4 pb-4 px-4 w-full border-none resize-none text-base md:text-lg font-agenda',
                )}
                value={comment}
                onChange={(ev) => setComment(ev.target.value)}
                placeholder="Share your thoughts..."
                maxRows={6}
                minRows={3}
              />
            </div>
          </div>

          {/* Checkout Button */}
          <div className="pt-4">
            <button
              className="cz-btn-brand w-full h-12 font-agenda-bold text-base uppercase disabled:bg-lightgray-999 disabled:cursor-not-allowed"
              onClick={handleCheckout}
              disabled={!amount || amount === 0}
            >
              Checkout
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default inject('uiStore', 'authStore')(observer(QRGiftCheckoutView));
